import { useTranslation } from 'react-i18next';
import { MetricCardProps } from './types/result-card.types';

// Icon circle diameter (matches w-11 = 44px)
const CIRCLE_PX = 44;
// How many px the bar overlaps behind the circle (visual layering effect)
const BAR_OVERLAP_PX = 11;
// Bar track start = circle right edge minus overlap
const BAR_LEFT_PX = CIRCLE_PX - BAR_OVERLAP_PX; // 33px
// Right-side padding inside the card so bar doesn't hug the edge
const BAR_RIGHT_PX = 8;

export const MetricCard = ({ title, barColor, barWidth, icon, isNotApplicable = false }: MetricCardProps) => {
    const { t } = useTranslation();
	const trackBg = isNotApplicable
		? '#9e9e9e' // Gray track for N/A metrics
		: undefined;
	const clampedBarWidth = Math.max(0, Math.min(100, barWidth));
	const trackInsetPx = BAR_LEFT_PX + BAR_RIGHT_PX;

	return (
		<div
			className="w-full h-full rounded-lg border border-[#cce0dc] px-4 py-3 flex flex-col gap-3"
			style={{
				background: 'linear-gradient(108.89deg, rgba(255,255,255,0.86) 0%, rgba(207,235,229,0.86) 100%)',
			}}
		>
			{/* Title */}
			<div className="text-sm font-semibold text-start leading-snug">
				{title}
			</div>

			{/* Bar + Icon row */}
			<div className="relative w-full mt-auto" style={{ height: CIRCLE_PX }}>
				{/* Track */}
				<div
					className="absolute rounded-full h-3"
					style={{
						top: '50%',
						transform: 'translateY(-50%)',
						left: BAR_LEFT_PX,
						right: BAR_RIGHT_PX,
						background: trackBg ?? '#ffffff',
					}}
				/>

				{/* Filled bar (hidden when not applicable) */}
				{!isNotApplicable && (
					<div
						className="absolute rounded-full h-3"
						style={{
							top: '50%',
							transform: 'translateY(-50%)',
							left: BAR_LEFT_PX,
							background: barColor,
							width: `calc((100% - ${trackInsetPx}px) * ${clampedBarWidth} / 100)`,
							maxWidth: `calc(100% - ${trackInsetPx}px)`,
						}}
					/>
				)}

				{/* Icon circle */}
				<div
					className="absolute left-0 top-0 rounded-full flex items-center justify-center"
					style={{
						width: CIRCLE_PX,
						height: CIRCLE_PX,
						background: barColor,
						boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
					}}
				>
					{icon}
				</div>

				{/* "N/A" label when not applicable */}
				{isNotApplicable && (
					<div
						className="absolute text-[10px] font-semibold text-[#ffffff] max-w-[calc(100%-60px)] overflow-hidden text-ellipsis whitespace-nowrap"
						style={{
							top: '50%',
							transform: 'translateY(-50%)',
							right: '16px',
						}}
					>
						{t(('survey.result.notApplicable'))}
					</div>
				)}
			</div>
		</div>
	);
};
